import { createClient as createBettingServiceSTOMPClient, getSTOMPClient } from './betting-service/stomp-client';
import {
	createBettingServiceApiClient,
	getBettingServiceApiClient
} from '~/components/sportsbook/lobby/api/betting-service/betting-service-api-client';
import {
	BetsResponse,
	CalculateCashoutResponse,
	CheckBetsRequest,
	PlaceBetsRequest,
	PlaceBetsResponse,
	SubmitCashoutRequest,
	SubmitCashoutResponse,
	UpdateAcceptModeRequest
} from '~/components/sportsbook/lobby/api/betting-service/betting-service-types';
import { BetStatus, OddsFormat } from '~/components/sportsbook/lobby/sportsbook-types';

type SetupParams = {
	franchiseCode: string;
	locale: string;
};

const ROUTE_SUBSCRIBE_TO_DELAYED_BETS = `/public/user/queue/bet/subscribeToDelayedBets`;
const ROUTE_SUBSCRIBE_TO_BET_UPDATES = `/public/user/queue/bets/subscribeToBetUpdates`;
const ROUTE_SUBSCRIBE_TO_OPEN_BETS_COUNT = `/public/user/queue/bets/subscribeToOpenBetsCount`;
const ROUTE_SUBSCRIBE_TO_CASHOUT_CALCULATE_ALL = `/public/user/queue/cashout/subscribeToCalculateAll`;
const ROUTE_SUBSCRIBE_TO_CASHOUT_DELAYED = `/public/user/queue/cashout/subscribeToDelayedCashouts`;

export const setUpBettingServiceApi = (params: SetupParams) => {
	const { franchiseCode, locale } = params;
	createBettingServiceApiClient(franchiseCode);

	const websocketHost = window.location.host;
	createBettingServiceSTOMPClient(
		`wss://${websocketHost}/sportsbook-betting-service/public/stomp/ws?franchiseCode=${franchiseCode}&locale=${locale}`
	);
};

export const refresh = async (franchiseCode: string, uuid: string) =>
	getBettingServiceApiClient().refresh(franchiseCode, uuid);
export const checkBets = async (req: CheckBetsRequest, abortSignal: AbortSignal) =>
	getBettingServiceApiClient().checkBets(req, abortSignal);
export const placeBets = async (req: PlaceBetsRequest) => getBettingServiceApiClient().placeBets(req);
export const updateAcceptMode = async (req: UpdateAcceptModeRequest) =>
	getBettingServiceApiClient().updateAcceptMode(req);
export const submitCashout = async (req: SubmitCashoutRequest) => getBettingServiceApiClient().submitCashout(req);
export const generateBetBuilderUrl = async (betBuilderId: string, oddsFormat: OddsFormat, hasReversedOrder: boolean) =>
	getBettingServiceApiClient().generateBetBuilderUrl(betBuilderId, oddsFormat, hasReversedOrder);
export const getBetBuilderItem = async (betId: string) => getBettingServiceApiClient().getBetBuilderItem(betId);
export const fetchBets = async (
	betStatus: BetStatus,
	startIndex: number,
	pageSize: number,
	startDate?: string,
	endDate?: string
) => getBettingServiceApiClient().fetchBets(betStatus, startIndex, pageSize, startDate, endDate);
export const fetchBet = async (betId: number) => getBettingServiceApiClient().fetchBet(betId);
export const fetchAcceptMode = async () => getBettingServiceApiClient().fetchAcceptMode();
export const listActiveBonuses = async () => getBettingServiceApiClient().listActiveBonuses();
export const subscribeToPlacementUpdates = (ref: string, onMessage: (data: PlaceBetsResponse) => void) =>
	getSTOMPClient().subscribe({
		ref,
		route: ROUTE_SUBSCRIBE_TO_DELAYED_BETS,
		onMessage,
		// eslint-disable-next-line no-console
		onError: console.warn,
		onSubscribed: () => {}
	});
export const unsubscribeFromPlacementUpdates = (ref: string) => getSTOMPClient().unsubscribe(ref);

export const disposeSubscriptionToCashoutInformation = (ref: string) => {
	getSTOMPClient().unsubscribe(ref);
};

export const createSubscriptionToCashoutInformation = (
	ref: string,
	onMessage: (data: CalculateCashoutResponse[]) => void,
	onError: (error: unknown) => void
) => {
	disposeSubscriptionToCashoutInformation(ref);
	getSTOMPClient().subscribe({
		ref,
		route: ROUTE_SUBSCRIBE_TO_CASHOUT_CALCULATE_ALL,
		onMessage,
		onError,
		onSubscribed: () => {}
	});
};

export const disposeSubscriptionToCashoutSubmits = (ref: string) => {
	getSTOMPClient().unsubscribe(ref);
};

export const createSubscriptionToCashoutSubmits = (ref: string, onMessage: (data: SubmitCashoutResponse) => void) => {
	disposeSubscriptionToCashoutSubmits(ref);
	getSTOMPClient().subscribe({
		ref,
		route: ROUTE_SUBSCRIBE_TO_CASHOUT_DELAYED,
		onMessage,
		// eslint-disable-next-line no-console
		onError: console.warn,
		onSubscribed: () => {}
	});
};

export const disposeSubscriptionToBets = (ref: string) => {
	getSTOMPClient().unsubscribe(ref);
};

export const createSubscriptionToBets = (
	ref: string,
	onMessage: (data: BetsResponse) => void,
	onError: (error: unknown) => void
) => {
	disposeSubscriptionToBets(ref);
	getSTOMPClient().subscribe({
		ref,
		route: ROUTE_SUBSCRIBE_TO_BET_UPDATES,
		onMessage,
		onError,
		onSubscribed: () => {}
	});
};

export const disposeSubscriptionToOpenBetsCount = (ref: string) => {
	getSTOMPClient().unsubscribe(ref);
};

export const createSubscriptionToOpenBetsCount = (
	ref: string,
	onMessage: (data: { unsettledBetsCount: number }) => void,
	onError: (error: unknown) => void
) => {
	disposeSubscriptionToOpenBetsCount(ref);
	getSTOMPClient().subscribe({
		ref,
		route: ROUTE_SUBSCRIBE_TO_OPEN_BETS_COUNT,
		onMessage: (num: number) => onMessage({ unsettledBetsCount: num }),
		onError,
		onSubscribed: () => {}
	});
};

export const fetchOpenBetsCount = async () => getBettingServiceApiClient().fetchOpenBetsCount();
export const calculateCashOutAllOpenBets = async () => getBettingServiceApiClient().calculateCashOutAllOpenBets();

// Avoid propagating betting-service-client changes, they will replace their own singletons.
if (import.meta.webpackHot) {
	import.meta.webpackHot.accept(
		['./betting-service/betting-service-api-client.ts', './betting-service/stomp-client.ts'],
		() => {}
	);
}
